import { useEffect, useRef } from "react";
import {
  syncPushTokenIfPossible,
  type NotificationPermissionState,
} from "../../../services/firebase";
import type { NotificationPermissionStatus } from "../../../services/notificationPermissionStatus";

/**
 * On returning to the foreground (tab visible / window focused), re-read the
 * OS notification permission and, if it is authorized, sync the push token.
 * The very first authorization after leaving for the OS settings needs a
 * forced token sync (`settingsReturn`); subsequent foregrounds only refresh
 * (`foreground`).
 *
 * Extracted verbatim from the legacy `SettingsEditAccountPage` so the new
 * (effect-free) `NotificationsView` business component keeps this behaviour
 * without owning an effect itself.
 */
export function useNotificationForegroundSync(
  refreshPermissionState: () => Promise<NotificationPermissionState>,
  currentStatus: NotificationPermissionStatus | null
): void {
  const previousStatusRef = useRef<NotificationPermissionStatus | null>(null);

  useEffect(() => {
    previousStatusRef.current = currentStatus;
  }, [currentStatus]);

  useEffect(() => {
    const refreshFromSystem = () => {
      if (document.visibilityState === "hidden") return;

      void refreshPermissionState().then((nextState) => {
        // pushOptOut sync is handled globally by useFirebaseUser.
        if (nextState.status !== "authorized") return;

        const previousStatus = previousStatusRef.current;
        void syncPushTokenIfPossible(
          previousStatus === "authorized"
            ? { reason: "foreground" }
            : { force: true, reason: "settingsReturn" }
        ).catch(console.error);
      });
    };

    refreshFromSystem();
    document.addEventListener("visibilitychange", refreshFromSystem);
    window.addEventListener("focus", refreshFromSystem);
    return () => {
      document.removeEventListener("visibilitychange", refreshFromSystem);
      window.removeEventListener("focus", refreshFromSystem);
    };
  }, [refreshPermissionState]);
}
